import { CellSpacing } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { Cell, overflowClassName } from '@cbhq/cds-web/cells/Cell';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextLabel2 } from '@cbhq/cds-web/typography';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useFindOrCreateToken } from ':cloud/hooks/useFindOrCreateToken';
import { IPlatformToken } from ':cloud/types/ts_types';
import { getFormattedTimeZoneLocal } from ':cloud/utils/date';
import APIKeyTabs from ':cloud/widgets/access/api/APIKeyTabs';
import Permission from ':cloud/widgets/auth/';
import { OWNER_ADMIN_ROLES } from ':cloud/widgets/auth/util';
import { ClipboardIcon } from ':cloud/widgets/sharedcomponents/ClipboardIcon';

interface PreviousTokenInformationProps {
  previousToken: IPlatformToken;
}

function PreviousTokenInformation({ previousToken }: PreviousTokenInformationProps) {
  if (!previousToken) {
    return null;
  }

  const previousTokenExpiresTime = new Date(previousToken.expiresTime);

  return (
    <TextLabel2 as="p" color="foregroundMuted">
      Your previous key{' '}
      <Box
        as="span"
        background="backgroundAlternate"
        display="inline-block"
        spacingHorizontal={0.5}
        spacingVertical={0.5}
        borderRadius="roundedSmall"
      >
        {previousToken.id}
      </Box>{' '}
      {previousTokenExpiresTime < new Date() ? 'expired on' : 'will expire on'}{' '}
      {getFormattedTimeZoneLocal(previousTokenExpiresTime)}.
    </TextLabel2>
  );
}

interface TokenProps {
  onRotatePress: (clientKeyId: string) => void;
  onConfigurePress: (clientKeyId: string) => void;
}

const spacing: CellSpacing = { spacing: 0, spacingHorizontal: 2 };

export function Token({ onRotatePress, onConfigurePress }: TokenProps) {
  const { selectedProject } = useProject();

  const { token, previousToken, isLoading } = useFindOrCreateToken(
    selectedProject?.organizationId,
    selectedProject?.id,
    selectedProject?.isTestnet,
  );

  return (
    <VStack gap={4}>
      <HStack gap={4} justifyContent="space-between" height={48}>
        <APIKeyTabs />
        <Permission permission={OWNER_ADMIN_ROLES}>
          <HStack gap={2}>
            <VStack justifyContent="center">
              <Button onPress={() => onRotatePress(token.id)}>Rotate</Button>
            </VStack>
            <VStack justifyContent="center">
              <Button onPress={() => onConfigurePress(token.id)}>Configure</Button>
            </VStack>
          </HStack>
        </Permission>
      </HStack>
      <HStack gap={2}>
        <Icon name="questionMark" color="foregroundMuted" size="m" />
        <TextLabel2 as="p">
          The client API key is intended for usage in your front-end components, such as with
          OnchainKit and the RPC endpoint. It cannot access sensitive account information,
          portfolios, or funds.
        </TextLabel2>
      </HStack>
      {isLoading ? (
        <>
          <Fallback width={100} height={44} percentage shape="squircle" />
          <Fallback width={40} height={24} percentage shape="squircle" />
        </>
      ) : (
        <>
          <HStack
            width="100%"
            gap={6}
            alignItems="center"
            background="backgroundAlternate"
            borderRadius="rounded"
          >
            <Cell
              priority="end"
              innerSpacing={spacing}
              outerSpacing={spacing}
              detail={
                <Box justifyContent="flex-end">
                  <ClipboardIcon text={token.id} iconSize="s" />
                </Box>
              }
            >
              <Box borderRadius="roundedSmall" display="flex" spacing={2} flexGrow={1}>
                <TextBody
                  as="p"
                  color="foregroundMuted"
                  overflow="wrap"
                  className={overflowClassName}
                >
                  {token.id}
                </TextBody>
              </Box>
            </Cell>
          </HStack>
          <PreviousTokenInformation previousToken={previousToken} />
        </>
      )}
    </VStack>
  );
}
